// context/AuthContext.jsx
import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import { setTokens, clearTokens } from "./AxiosInstance";
import { useLocation, useNavigate } from "react-router-dom";
import config from "../Configurations/APIConfig";
import apiService from "../Configurations/APIService";
import { jwtDecode } from "jwt-decode";
import { isPlanExpired } from "../Layout/dateUtils";

const AuthContext = createContext();
export { AuthContext }; // TEMP-VERIFY-REMOVE
export const AuthProvider = ({ children }) => {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const abortControllerRef = useRef(null);

  // Only treat real auth failures (invalid/expired session) as a reason to log out.
  // Network errors, timeouts, and 5xx responses are transient and should not clear a valid session.
  const isAuthError = (err) =>
    err?.response?.status === 401 || err?.response?.status === 403;

  // Define special company types that should NOT go to /dashboard
  const specialCompanyTypes = ["VISA", "SERIESFAIR", "DMC", "HOTEL", "SUPPLIER"];

  // Extract profile mapping to helper function to avoid duplication
  const mapProfileToUser = (profile) => ({
    id: profile.userId,
    cid: profile.companyId,
    bid: profile.branchId,
    role: profile.roleName,
    name: profile.name,
    email: profile.email,
    phoneNumber: profile.phoneNumber,
    country: profile.country,
    currency: profile.currency,
    logo: profile.logo,
    brandName: profile.brandName,
    companyname: profile.companyName,
    companygstno: profile.companyGSTNo,
    companypancard: profile.companyPanCard,
    companyaddress: profile.companyAddress,
    companyEmail: profile.companyEmail,
    companyPhone: profile.companyPhone,
    companyWhatsappnumber: profile.companyWhatsappNumber,
   
    connects: profile.connects,
    isRenewal: profile.isRenewal,
    planType: profile.planType,
    endDate: profile.endDate,
  
    whatsAppNumber: profile.whatsAppNumber,
    isSupplierAPI: profile.isSupplierAPI,
    parentCompanyId: profile.parentCompanyId,
    companyType: profile.companyType,
    walletBalance: profile.walletBalance,
    entityType: profile.entityType,
    isKYC: profile.isKYC,
    isB2B: profile.isB2B,
    isB2C: profile.isB2C,
    isB2E: profile.isB2E,
    isPaymentGateway:profile.isPaymentGateway
  });

  const handleNavigationAfterLogin = (profile) => {
    if (isPlanExpired(profile.endDate)) {
      navigate("/subscriptionplans");
      return;
    }

    if (specialCompanyTypes.includes(profile.companyType)) {
      switch(profile.companyType) {
        case "SUPPLIER":
        case "DMC":
          navigate("/holidaydashboard");
          break;
        case "VISA":
          navigate("/visadashboard");
          break;
        case "SERIESFAIR":
          navigate("/seriesfare/dashboard");
          break;
        case "HOTEL":
          navigate("/hoteldashboard");
          break;
        default:
          navigate("/");
      }
    } else {
      navigate("/flight/search");
    }
  };
const getProfileAndUpdateState = async () => {
    try {
      const profile = await apiService.getSingle(config.GETUSERPROFILE);
      setUser({
        id: profile.userId,
        cid: profile.companyId,
        bid: profile.branchId,
        role: profile.roleName,
        name: profile.name,
        email: profile.email,
        phoneNumber: profile.phoneNumber,
        country: profile.country,
        currency: profile.currency,
        logo: profile.logo,
        brandName: profile.brandName,
        companyname: profile.companyName,
        companygstno: profile.companyGSTNo,
        companypancard: profile.companyPanCard,
        companyaddress: profile.companyAddress,
      
        connects: profile.connects,
        isRenewal: profile.isRenewal,
        planType: profile.planType,
        endDate: profile.endDate,
       
        whatsAppNumber: profile.whatsAppNumber,
        isSupplierAPI: profile.isSupplierAPI,
        parentCompanyId: profile.parentCompanyId,
        companyType: profile.companyType,
        walletBalance: profile.walletBalance,
        entityType: profile.entityType,
        isKYC: profile.isKYC,
        isB2B: profile.isB2B,
        isB2C: profile.isB2C,
        isB2E: profile.isB2E,
        isPaymentGateway:profile.isPaymentGateway

      });
      return profile;
    } catch (err) {
      console.error("Failed to fetch user profile:", err);
      if (isAuthError(err)) {
        await logout(); // Only logout when the session itself is invalid
      }
    }
  };
  useEffect(() => {
    const controller = new AbortController();
    abortControllerRef.current = controller;
    let isMounted = true;

    const initializeAuth = async () => {
      // Only "/unauthorized" is skipped — "/" and "/login" also need session
      // rehydration so an already-logged-in user isn't shown the login page.
      const skipPaths = ["/unauthorized"];
      if (skipPaths.includes(location.pathname)) {
        if (isMounted) setLoading(false);
        return;
      }

      // If user is already loaded in state, skip the refresh flow
      if (user) {
        if (isMounted) setLoading(false);
        return;
      }

      try {
        const accessToken = localStorage.getItem("accessToken");
        const refreshToken = localStorage.getItem("refreshToken");

        // If no tokens stored, user is not logged in
        if (!refreshToken) {
          if (isMounted) setLoading(false);
          return;
        }

        // If access token is expired or missing, refresh it
        if (!accessToken || isTokenExpired(accessToken)) {
          const res = await apiService.post(config.REFRESH, { refreshToken });
          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = res;

          localStorage.setItem("accessToken", newAccessToken);
          localStorage.setItem("refreshToken", newRefreshToken);

          setTokens({
            accessToken: newAccessToken,
            refreshToken: newRefreshToken,
          });
        }

        // Fetch profile after ensuring valid token
        if (controller.signal.aborted) return;
        const profile = await apiService.getSingle(config.GETUSERPROFILE);

        if (isMounted && !controller.signal.aborted) {
          const userData = mapProfileToUser(profile);
          setUser(userData);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        console.error("Auth initialization failed:", err);
        if (isMounted && isAuthError(err)) await logout();
      } finally {
        if (isMounted) setLoading(false);
      }
    };

    initializeAuth();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, []);

  function isTokenExpired(token) {
    if (!token) return true;
    try {
      const { exp } = jwtDecode(token); // exp is in seconds
      const now = Date.now() / 1000;
      return exp < now; // expired
    } catch (e) {
      return true;
    }
  }

  const login = async ({ accessToken, refreshToken }) => {
    if (!accessToken || !refreshToken) {
      console.error("Missing accessToken or refreshToken");
      return;
    }

    try {
      // Store both tokens
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      setTokens({
        accessToken: accessToken,
        refreshToken: refreshToken,
      });

      // Fetch user profile
      const profile = await apiService.getSingle(config.GETUSERPROFILE);
      const userData = mapProfileToUser(profile);
      setUser(userData);

      // Handle navigation based on company type and plan status
      handleNavigationAfterLogin(profile);
    } catch (err) {
      console.error("Login failed:", err);
      if (isAuthError(err)) await logout();
    }
  };

  const logout = async () => {
    // Abort any pending requests
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    clearTokens();
    setUser(null);
    navigate("/login", { replace: true });
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, logout,refreshUserProfile: getProfileAndUpdateState }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);